import React, { useEffect, useCallback, FC } from 'react'
import { requestCommon, jsonResponseExtractor } from 'live/lib/utils'
import i18n from 'live/services/i18n'
import WebSettingsCard from 'live/components/ui/admin/web-settings-card'
import { useWebSettingsState } from 'live/settings/useWebSettingsState'
import globalNotification from 'live/services/global-notification'
import FormGroup from 'live/components/forms/ui/form-group'

const BASE_URL = '/services/plugin-annotations-templates/annotations-templates/config'

type QueryParams = {
    templateLimit: number
}

const TemplateSettings: FC = () => {
    const [queryParams, setQueryParams] = React.useState<QueryParams>({ templateLimit: -1 })
    const { state, actions } = useWebSettingsState(queryParams)

    const fetchItems = useCallback(() => {
        api.get()
            .then(jsonResponseExtractor)
            .then(queryParams => {
                setQueryParams((queryParams as unknown) as QueryParams)
                actions.setPersistedData(queryParams)
            })
    }, [actions])

    useEffect(fetchItems, [])

    const onSave = React.useCallback(() => {
        actions.submit()

        api.persist(queryParams)
            .then(jsonResponseExtractor)
            .then(queryParams => {
                setQueryParams((queryParams as unknown) as QueryParams)
                actions.setPersistedData(queryParams)

                actions.saveSuccess(queryParams)
                globalNotification.success(i18n('Settings successfully saved'))
            })
            .catch(async r => {
                actions.setError(i18n('There was an error saving the query params'))
            })
    }, [actions, queryParams])

    return (
        <WebSettingsCard
            name="template-settings"
            className="live-feature mb-15"
            open={state.open}
            error={state.submitted && state.open ? state.error || undefined : undefined}
            saved={state.open && state.saved}
            save={onSave}
            toggle={actions.toggle}
            onCancel={() => {
                actions.setError()
                setQueryParams(state.persistedData)
            }}
        >
            <div style={{ marginBottom: '35px' }}>
                <div className="live-feature__header">
                    <h2 className="flex-1">{i18n('Template settings')}</h2>
                </div>
                <FormGroup
                    type="number"
                    hideInput={!state.open}
                    value={queryParams.templateLimit}
                    label={i18n('Number of templates')}
                    validationState={state.open && state.submitted ? (state.error ? 'error' : 'success') : undefined}
                    onChange={({ target }) => {
                        setQueryParams({
                            templateLimit: target.value
                        })
                    }}
                />
                <span className="help-block">
                    {i18n('This configuration will limit annotation templates per environment')}.
                </span>
            </div>
        </WebSettingsCard>
    )
}

const api = {
    get() {
        return fetch(BASE_URL, {
            credentials: 'same-origin'
        })
    },
    persist(settings: QueryParams) {
        return fetch(BASE_URL, {
            method: 'POST',
            ...requestCommon,
            body: JSON.stringify(settings)
        })
    }
}

export default {
    persist: api.persist,
    configComponent: TemplateSettings,
    section: i18n('Advanced annotations'),
    name: i18n('Template settings')
}
